<template>
  <div>
    <input
      aria-labelledby="search-label"
      name="search"
      v-model="search"
      class="woo-search-input"
      placeholder="Search..."
      :style="{ 'z-index': 1000, position: 'relative', 'margin-top': '20px', display: 'none' }"
      ref="search">
    <v-expansion-panels accordion :multiple="hasQuery" flat v-model="currentPanelModel">
      <v-expansion-panel
        v-for="(group, $groupIndex) in sections"
        :key="$groupIndex"
        class="woo-tutorial-group"
      >
        <v-expansion-panel-header align="left" class="woo-tutorial-group-header">
          <span class="woo-tutorial-group-header-number">
            {{ $groupIndex + 1 }}.
          </span>
          <span>{{ group.display_name }}</span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="woo-tutorial-group-conent">
          <div
            class="lite-step"
            v-if="tutorial.isLite && group.content"
            v-html="group.content"/>
          <v-list flat>
            <v-list-item
              v-for="(step, $stepIndex) in group.steps"
              :key="$stepIndex"
              class="woo-tutorial-group-step"
              v-ripple="{ class: `woo-ripple` }"
              @click="playStep(step)"
              :class="{
                inprogress: isStepInProgress(step),
              }"
            >
              <div style="width: 100%">
                <div style="display: flex;">
                  <v-list-item-icon>
                    <v-icon
                      class="woo-tutorial-group-step-icon"
                      :class="{
                        completed: isStepCompleted(step),
                      }"
                      >{{ getStepIcon(step) }}</v-icon
                    >
                    <div
                      class="icon-progress"
                      v-if="isStepInProgress(step)"
                      :style="{ width: getStepProgress(step) }"
                    ></div>
                  </v-list-item-icon>
                  <v-list-item-content
                    align="left"
                    class="woo-tutorial-group-step-title"
                  >
                    {{ step.display_name }}
                    <span class="subtitle" v-if="isStepInProgress(step)">
                      Just played</span
                    >
                  </v-list-item-content>
                </div>
                <div
                  class="lite-step"
                  v-if="tutorial.isLite && step.id === currentLiteStepId"
                  v-html="step.content"/>
              </div>
            </v-list-item>
          </v-list>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>
<script>
import { HANDS } from '@/constants';
import Db from '@/helpers/Db';
import HelperMethods from '@/helpers/HelperMethods';

export default {
  name: 'TableOfContents',
  data() {
    return {
      search: '',
      currentPanel: undefined,
      currentLiteStepId: undefined,
    };
  },
  props: {
    passive: {
      type: Boolean,
    },
  },
  computed: {
    currentPanelModel() {
      if (this.hasQuery) {
        return this.tutorial.sections.map((s, i) => i);
      }

      return this.currentPanel;
    },
    hasQuery() {
      return this.search.length > 0
    },
    tutorial() {
      return Db.getFullTutorialBySlug(this.$route.params.id);
    },
    hand() {
      return this.$store.state.hands[this.tutorial?.id] || HANDS.RIGHT;
    },
    sections() {
      let sections = this.tutorial.sections;

      // Lite tutorials
      if (this.tutorial.isLite) {
        sections = this.tutorial[this.hand].sections.map(s => ({
          id: HelperMethods.makeid(10),
          ...s,
          steps: s.steps.map(t => ({
            ...t,
            id: HelperMethods.makeid(10)
          }))
        }));
      }

      const match = s => HelperMethods.matchText({
        text: s.display_name,
        query: this.search,
      });

      return sections.filter(s => {
        return match(s) || s.steps.some(t => match(t))
      }).map(s => ({
        ...s,
        steps: s.steps.filter(t => {
          return match(t) || match(s)
        })
      }));
    },
  },
  mounted() {
    const { s } = HelperMethods.getLastPlayedVideo();

    // User was watching something
    if (s) {
      const watchingStep = Db.findStepById({
        tutorial: this.tutorial,
        stepId: s,
      });

      // Probably cached some old ID
      if (!watchingStep) {
        this.currentPanel = 0;
        return;
      }

      this.currentPanel = this.tutorial.sections.findIndex(section => section.id === watchingStep.sectionId);
      
      setTimeout(() => {
        let el = this.$el.getElementsByClassName('inprogress')[0];
        el.scrollIntoView(true);
      }, 200);
    } else {
      this.currentPanel = 0;
    }
  },
  methods: {
    getStepIcon(step) {
      if (this.tutorial.isLite) {
        return 'mdi-text';
      } else if (this.isStepCompleted(step)) {
        return 'mdi-check';
      } else if (step.microsteps) {
        return 'mdi-play';
      } else {
        return 'mdi-text';
      }
    },
    playStep(step) {
      if (this.tutorial.isLite) {
        this.currentLiteStepId = this.currentLiteStepId === step.id ? null : step.id;
        return;
      }

      const { v, s } = HelperMethods.getLastPlayedVideo();
      const lastStep = step.id === s;
      
      this.$router.push({
        query: {
          v: lastStep
            ? v
            : step.microsteps[0].id,
          s: step.id,
        },
      }).catch();
    },
    isStepCompleted(step) {
      return HelperMethods.isStepCompleted(step)
    },
    isStepInProgress(step) {
      if (this.tutorial.isLite) return false;
      return HelperMethods.getLastPlayedVideo().s === step.id;
    },
    getStepProgress(step) {
      if (this.isStepInProgress(step)) {
        const currentVideoIndex = step.microsteps.findIndex(ms => {
          return ms.id === HelperMethods.getLastPlayedVideo().v;
        });

        return `${((currentVideoIndex + 1) / step.microsteps.length) * 100}%`;
      } else {
        return '0%';
      }
    },
  },
};
</script>
<style lang="scss">
.lite-step {
  text-align: left; padding: 20px 0px;
  img {
    width: 100%;
    height: auto;
    margin: 20px 0px 10px 0px;
  }

  h2 {
    margin: 20px 0px 10px 0px;
  }

  .row {
    box-sizing: border-box;
    padding: 20px;
  }

  .one-half-column-left {
    margin-bottom: 2em;
    padding: 10px;
    width: 48%;
    float: left;
  }

  .one-half-column-right {
    margin-bottom: 2em;
    padding: 10px;
    width: 48%;
    float: right;
  }

  .one-quarter-column {
    padding: 10px;
    width: 23%;
    margin-right: 2%;
    float: left;
    margin-bottom: 1em;
  }

  .one-half-column {
    padding: 10px;
    width: 48%;
    margin-right: 2%;
    float: left;
    margin-bottom: 1em;
  }

  h3 {
    margin: 20px 0px 10px 0px;
  }

  .ctr {
    text-align: center;
  }

  iframe {
    margin-bottom: 20px;
  }

  b {
    font-weight: 900 !important;
  }
}
</style>
<style lang="scss" scoped>
.woo {
  &-tutorial {
    &-group {
      &-header {
        padding: 1rem 0;
        font-weight: 900;
        font-size: 1.125rem;
        &-number {
          width: 2.5rem;
          flex: none !important;
          text-align: center;
        }
      }
      &-step {
        padding: 0.5rem 0.5rem !important;
        min-height: 2.5rem;
        border-radius: 0.5rem;
        &.inprogress {
          font-weight: bold;
        }
        .subtitle {
          font-family: Roboto;
          font-size: 0.75rem;
          font-weight: 400;
          line-height: 0.875rem;
          letter-spacing: 0em;
          text-align: left;
          color: rgba(0, 0, 0, 0.54);
        }
        &-icon {
          width: 2.5rem;
          height: 2.5rem;
          background-color: #004d69;
          color: #fff !important;
          border-radius: 0.25rem;
          font-size: 1.125rem !important;
          &.completed {
            background-color: #2cc1bc;
          }
        }
        .icon-progress {
          background-color: #2cc1bc;
          width: 100%;
          position: absolute;
          bottom: 0;
          height: 3px;
        }
        &-title {
          padding: 0 !important;
          font-weight: 700;
        }
      }
    }
  }
}
.v-item-group {
  z-index: 1;
}
.v-list-item__icon {
  margin: 0 1rem 0 0 !important;
  position: relative;
}
.v-list--flat {
  padding: 0 !important;
}
</style>
<style lang="scss">
.woo-ripple {
  background: rgba(44, 193, 188, 0.1);
}
.woo {
  &-tutorial {
    //Overrides
    .v-expansion-panel-content__wrap {
      padding: 0 !important;
    }
    .v-expansion-panel-header__icon {
      .v-icon {
        color: black !important;
      }
    }
  }
}
</style>
